import { ComponentType, lazy } from 'react';

const RELOAD_KEY = 'chunk-reload-at';
// A second chunk failure within this window means a reload didn't help, so the error is shown instead of looping
const RELOAD_WINDOW_MS = 60 * 1000;

const isChunkLoadError = (error: unknown) =>
  error instanceof Error &&
  (error.name === 'ChunkLoadError' || /Loading (CSS )?chunk [\w-]+ failed/i.test(error.message));

// A tab opened before a deploy can still run the old main.js, which asks for chunks that the new build no longer has.
// Reloading once picks up the new index.html and its chunks instead of leaving a blank page.
const reloadOnce = (): boolean => {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    if (Date.now() - last < RELOAD_WINDOW_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // Without sessionStorage there is no loop guard, so don't reload
    return false;
  }
  window.location.reload();
  return true;
};

// Drop-in replacement for React.lazy that reloads the page once when a chunk fails to load
export const lazyWithReload = <T extends ComponentType<any>>(factory: () => Promise<{ default: T }>) =>
  lazy(() =>
    factory().catch((error) => {
      if (isChunkLoadError(error) && reloadOnce()) {
        // Keep Suspense showing its fallback while the page reloads
        return new Promise<{ default: T }>(() => {});
      }
      throw error;
    }),
  );
